import { useEffect, useMemo, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useBranding } from '@/contexts/BrandContext';
import { useNavPreset } from '@/hooks/usePresets';
import { usePageTypeContext } from '@/contexts/PageTypeContext';
import { useMediaQuery } from '@/hooks/useMediaQuery';
import PublicNavMobileShell from './PublicNavMobileShell';
import PublicNavDesktop from './PublicNavDesktop';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const CONTAINER_WIDTH = {
  full: 'max-w-none',
  wide: 'max-w-screen-2xl',
  default: 'max-w-7xl',
};

const HEIGHT_CLASS = {
  compact: 'h-12',
  default: 'h-16',
  tall: 'h-20',
};

const LOGO_HEIGHT_PX = {
  sm: 24,
  md: 32,
  lg: 40,
  xl: 48,
};

function resolveLogoUrl(design, presetLogos, branding, isDark) {
  const logoVariant = design.logo_variant || 'auto';
  if (logoVariant === 'icon') return presetLogos.logo_icon || branding?.logo_icon;
  if (logoVariant === 'light') return presetLogos.logo_light || branding?.logo_light;
  if (logoVariant === 'dark') return presetLogos.logo_dark || branding?.logo_dark;
  if (isDark) return presetLogos.logo_dark || branding?.logo_dark || presetLogos.logo_light || branding?.logo_light;
  return presetLogos.logo_light || branding?.logo_light || presetLogos.logo_dark || branding?.logo_dark;
}

function resolveDashboardPath() {
  try {
    const u = JSON.parse(localStorage.getItem('user') || '{}');
    if (u?.role === 'superadmin') return '/superadmin';
    if (u?.role === 'company_admin') return '/dashboard';
    if (u?.role === 'employee') return '/my-dashboard';
  } catch { /* ignore */ }
  return '/dashboard';
}

export default function PublicNav({ pageType: pageTypeProp = null, presetOverrideId = null }) {
  const navRef = useRef(null);
  const isMobile = useMediaQuery('(max-width: 767px)');
  const { branding } = useBranding();
  const { pageType: ctxPageType, navPresetOverride: ctxNavOverride } = usePageTypeContext();
  const pageType = pageTypeProp || ctxPageType || 'marketing';
  const overrideId = presetOverrideId || ctxNavOverride || null;
  const { preset, preparedLinks, loaded } = useNavPreset(pageType, overrideId);

  const design = preset?.design || {};

  // Apply scroll styling via DOM classList — avoids re-rendering link/menu components on scroll.
  useEffect(() => {
    const nav = navRef.current;
    if (!nav) return undefined;

    const applyScrollClass = () => {
      nav.classList.toggle('public-nav-scrolled', window.scrollY > 10);
    };

    applyScrollClass();
    window.addEventListener('scroll', applyScrollClass, { passive: true });
    return () => window.removeEventListener('scroll', applyScrollClass);
  }, [preset?.id]);

  const navChrome = useMemo(() => {
    const isFloating = design.position === 'fixed' || design.position === 'sticky';
    const isDark = design.text_color === 'light' || (design.text_color === 'auto' && design.background === 'dark');

    let baseBg = '';
    switch (design.background) {
      case 'dark': baseBg = 'bg-slate-900'; break;
      case 'glass': baseBg = design.blur_on_scroll ? 'public-nav-glass' : 'bg-white/80'; break;
      case 'gradient': baseBg = 'bg-gradient-to-r from-indigo-50 via-white to-indigo-50'; break;
      case 'transparent': baseBg = 'bg-transparent'; break;
      case 'white':
      default: baseBg = design.blur_on_scroll ? 'public-nav-white-blur' : 'bg-white';
    }

    const positionClass = isFloating
      ? `${design.position === 'fixed' ? 'fixed' : 'sticky'} top-0 left-0 right-0 z-50 public-nav-bar`
      : 'relative w-full public-nav-bar';

    const transparentTop = design.transparent_at_top ? 'public-nav-transparent-top' : '';
    const shadowOnScroll = design.shadow_on_scroll ? 'public-nav-shadow-on-scroll' : '';
    const borderBottom = design.border_bottom ? 'border-b border-indigo-50' : '';

    return {
      isDark,
      className: [positionClass, baseBg, transparentTop, shadowOnScroll, borderBottom].filter(Boolean).join(' '),
      heightClass: HEIGHT_CLASS[design.height || 'default'] || HEIGHT_CLASS.default,
      containerWidth: CONTAINER_WIDTH[design.container_width || 'default'] || CONTAINER_WIDTH.default,
      logoCenter: design.logo_position === 'center',
      linksAlignment: design.alignment || 'right',
    };
  }, [design]);

  const logoMeta = useMemo(() => {
    const logoHeightPx = design.logo_height === 'custom'
      ? (design.logo_height_px || 32)
      : (LOGO_HEIGHT_PX[design.logo_height || 'md'] || 32);
    const logoMaxWidth = design.logo_max_width || 160;
    const presetLogos = preset?.logos || {};
    const rawUrl = resolveLogoUrl(design, presetLogos, branding, navChrome.isDark);
    const logoUrl = rawUrl ? (rawUrl.startsWith('http') ? rawUrl : `${BACKEND_URL}${rawUrl}`) : null;
    return {
      logoUrl,
      logoAlt: branding?.site_name || 'Provisoft',
      logoHeightPx,
      logoMaxWidth,
      siteName: branding?.site_name || 'Provisoft',
    };
  }, [design, preset?.logos, branding, navChrome.isDark]);

  const logoEl = useMemo(() => (
    <Link to="/" className="flex items-center gap-2 shrink-0" data-testid="nav-logo">
      {logoMeta.logoUrl ? (
        <img
          src={logoMeta.logoUrl}
          alt={logoMeta.logoAlt}
          style={{ height: `${logoMeta.logoHeightPx}px`, maxWidth: `${logoMeta.logoMaxWidth}px` }}
          className="w-auto object-contain"
        />
      ) : (
        <span className={`text-xl font-bold ${navChrome.isDark ? 'text-white' : 'text-indigo-600'}`} style={{ fontFamily: 'Outfit, sans-serif' }}>
          {logoMeta.siteName}
        </span>
      )}
    </Link>
  ), [logoMeta, navChrome.isDark]);

  const mobileDesign = useMemo(() => ({
    show_login_button: !!design.show_login_button,
    show_signup_cta: !!design.show_signup_cta,
    cta_label: design.cta_label || '',
    cta_link: design.cta_link || '',
  }), [design.show_login_button, design.show_signup_cta, design.cta_label, design.cta_link]);

  const isLoggedIn = !!localStorage.getItem('access_token');
  const dashboardPath = useMemo(() => resolveDashboardPath(), []);

  const skeletonChrome = useMemo(() => ({
    isDark: false,
    className: 'fixed top-0 left-0 right-0 z-50 public-nav-bar bg-white h-16',
    heightClass: HEIGHT_CLASS.default,
    containerWidth: CONTAINER_WIDTH.default,
  }), []);

  const skeletonLogoMeta = useMemo(() => {
    const rawUrl = branding?.logo_light || branding?.logo_dark;
    const logoUrl = rawUrl ? (rawUrl.startsWith('http') ? rawUrl : `${BACKEND_URL}${rawUrl}`) : null;
    return {
      logoUrl,
      logoAlt: branding?.site_name || '[REDACTED]',
      logoHeightPx: LOGO_HEIGHT_PX.md,
      logoMaxWidth: 160,
      siteName: branding?.site_name || '[REDACTED]',
    };
  }, [branding]);

  if (!loaded) {
    return (
      <>
        <style>{`
          .public-nav-bar { transition: background-color 150ms ease, box-shadow 150ms ease, backdrop-filter 150ms ease; }
        `}</style>
        <nav ref={navRef} className={skeletonChrome.className} data-testid="public-nav">
          {isMobile ? (
            <PublicNavMobileShell
              preparedLinks={[]}
              design={{ show_login_button: true, show_signup_cta: true, cta_label: '', cta_link: '' }}
              isDark={skeletonChrome.isDark}
              containerWidth={skeletonChrome.containerWidth}
              heightClass={skeletonChrome.heightClass}
              logoUrl={skeletonLogoMeta.logoUrl}
              logoAlt={skeletonLogoMeta.logoAlt}
              logoHeightPx={skeletonLogoMeta.logoHeightPx}
              logoMaxWidth={skeletonLogoMeta.logoMaxWidth}
              siteName={skeletonLogoMeta.siteName}
              isLoggedIn={isLoggedIn}
              dashboardPath={dashboardPath}
            />
          ) : (
            <div className={`${skeletonChrome.containerWidth} mx-auto px-4 sm:px-6 lg:px-8`}>
              <div className={`flex items-center justify-between ${skeletonChrome.heightClass}`}>
                <Link to="/" className="flex items-center gap-2 shrink-0" data-testid="nav-logo">
                  {skeletonLogoMeta.logoUrl ? (
                    <img
                      src={skeletonLogoMeta.logoUrl}
                      alt={skeletonLogoMeta.logoAlt}
                      style={{ height: `${skeletonLogoMeta.logoHeightPx}px`, maxWidth: `${skeletonLogoMeta.logoMaxWidth}px` }}
                      className="w-auto object-contain"
                    />
                  ) : (
                    <span className="text-xl font-bold text-indigo-600" style={{ fontFamily: 'Outfit, sans-serif' }}>
                      {skeletonLogoMeta.siteName}
                    </span>
                  )}
                </Link>
              </div>
            </div>
          )}
        </nav>
      </>
    );
  }

  if (!preset || preset.hidden) return null;

  return (
    <>
      <style>{`
        .public-nav-bar { transition: background-color 150ms ease, box-shadow 150ms ease, backdrop-filter 150ms ease; }
        .public-nav-transparent-top:not(.public-nav-scrolled) { background: transparent !important; box-shadow: none !important; backdrop-filter: none !important; }
        .public-nav-white-blur.public-nav-scrolled { background-color: rgba(255,255,255,0.8); backdrop-filter: blur(24px); }
        .public-nav-glass.public-nav-scrolled { background-color: rgba(255,255,255,0.8); backdrop-filter: blur(24px); }
        .public-nav-shadow-on-scroll.public-nav-scrolled { box-shadow: 0 1px 2px 0 rgb(0 0 0 / 0.05); }
      `}</style>
      <nav ref={navRef} className={navChrome.className} data-testid="public-nav">
        {isMobile ? (
          <PublicNavMobileShell
            preparedLinks={preparedLinks}
            design={mobileDesign}
            isDark={navChrome.isDark}
            containerWidth={navChrome.containerWidth}
            heightClass={navChrome.heightClass}
            logoUrl={logoMeta.logoUrl}
            logoAlt={logoMeta.logoAlt}
            logoHeightPx={logoMeta.logoHeightPx}
            logoMaxWidth={logoMeta.logoMaxWidth}
            siteName={logoMeta.siteName}
            isLoggedIn={isLoggedIn}
            dashboardPath={dashboardPath}
          />
        ) : (
          <PublicNavDesktop
            links={preparedLinks}
            design={design}
            navChrome={navChrome}
            logoEl={logoEl}
            isLoggedIn={isLoggedIn}
            getDashboardPath={() => dashboardPath}
          />
        )}
      </nav>
    </>
  );
}
